import React from 'react';
import { Box, Typography } from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { ACCOUNT_COLORS, accountCardSx } from '../../../components/account/accountPageStyles';
import { CWY_IS_MAINNET } from '../config/cwyConfig';

interface CwyActionsCardProps {
  activeTab: 'request' | 'deposit';
  onTabChange: (tab: 'request' | 'deposit') => void;
  children: React.ReactNode;
}

export const CwyActionsCard: React.FC<CwyActionsCardProps> = ({
  activeTab,
  onTabChange,
  children,
}) => {
  return (
    <Box sx={accountCardSx}>
      <Box
        sx={{
          display: 'flex',
          gap: 0.5,
          bgcolor: ACCOUNT_COLORS.surface2,
          border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
          p: 0.5,
          borderRadius: '11px',
          mb: 2.5,
        }}
      >
        {(['request', 'deposit'] as const).map((tab) => (
          <Box
            key={tab}
            component="button"
            type="button"
            onClick={() => onTabChange(tab)}
            sx={{
              flex: 1,
              border: 'none',
              cursor: 'pointer',
              textAlign: 'center',
              py: 1.125,
              borderRadius: '8px',
              fontSize: 13.5,
              fontWeight: 600,
              fontFamily: ACCOUNT_COLORS.fontBody,
              color: activeTab === tab ? ACCOUNT_COLORS.text : ACCOUNT_COLORS.textLo,
              bgcolor: activeTab === tab ? ACCOUNT_COLORS.surface3 : 'transparent',
              boxShadow: activeTab === tab ? '0 1px 0 rgba(0,0,0,.2), inset 0 0 0 1px #E1E4EA' : 'none',
              transition: 'all .15s ease',
            }}
          >
            {tab === 'request' ? 'Request CWY' : 'Deposit to vault'}
          </Box>
        ))}
      </Box>

      {!CWY_IS_MAINNET && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.125,
            fontSize: 12,
            color: ACCOUNT_COLORS.warning,
            bgcolor: ACCOUNT_COLORS.warningDim,
            border: '1px solid rgba(224,165,55,.25)',
            px: 1.625,
            py: 1.125,
            borderRadius: ACCOUNT_COLORS.radiusSm,
            mb: 2.25,
            fontWeight: 500,
          }}
        >
          <WarningAmberOutlinedIcon sx={{ fontSize: 14 }} />
          Devnet only — these are test tokens, never real funds.
        </Box>
      )}

      {children}
    </Box>
  );
};

export const CwyFieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 13.5, color: ACCOUNT_COLORS.textLo, mb: 1.25, fontWeight: 500 }}>
    {children}
  </Typography>
);

export const CwyAmountField: React.FC<{
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  suffix?: string;
}> = ({ value, onChange, disabled, suffix = 'CWY' }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      bgcolor: ACCOUNT_COLORS.surface2,
      border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
      borderRadius: ACCOUNT_COLORS.radiusSm,
      px: 2.5,
      py: 2,
      mb: 1,
      transition: 'border-color .15s ease',
      '&:focus-within': { borderColor: ACCOUNT_COLORS.goldBright },
    }}
  >
    <Box
      component="input"
      type="text"
      inputMode="decimal"
      value={value}
      disabled={disabled}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
      sx={{
        flex: 1,
        border: 'none',
        outline: 'none',
        bgcolor: 'transparent',
        color: ACCOUNT_COLORS.text,
        fontFamily: ACCOUNT_COLORS.fontMono,
        fontSize: 26,
        fontWeight: 600,
      }}
    />
    <Typography sx={{ fontFamily: ACCOUNT_COLORS.fontMono, color: ACCOUNT_COLORS.textDim, fontSize: 15, fontWeight: 600 }}>
      {suffix}
    </Typography>
  </Box>
);

export const CwyFieldHint: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography sx={{ fontSize: 13, color: ACCOUNT_COLORS.textDim, mb: 2.5 }}>{children}</Typography>
);

export const CwyInfoNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      display: 'flex',
      gap: 1.25,
      bgcolor: ACCOUNT_COLORS.surface2,
      border: `1px solid ${ACCOUNT_COLORS.borderSoft}`,
      borderRadius: ACCOUNT_COLORS.radiusSm,
      px: 1.75,
      py: 1.5,
      mb: 2.25,
      fontSize: 12.5,
      color: ACCOUNT_COLORS.textLo,
      lineHeight: 1.55,
    }}
  >
    {children}
  </Box>
);
